"use client";

import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { useState } from "react";

export default function StockCard({ stock, index = 0 }) {
  const [quantity, setQuantity] = useState("1");
  const [paymentMethod, setPaymentMethod] = useState("Bank Transfer");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  // Utility function to format price
  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (isNaN(num)) return "0";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: num % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
  };

  const handleWhatsAppSubmit = () => {
    // Format the message with all plan details
    const message = `
Hello, I'm interested in investing in ${stock.name}.

Investment Details:
- Plan: ${stock.name}
- Location: ${stock.location}
- Investment Amount: $${formatPrice(stock.price)}
- Return Rate: ${stock.return_rate}
- Duration: ${stock.duration}
- Payment Method: ${paymentMethod}

Please contact me to complete this investment.
    `.trim();

    // Create WhatsApp URL with the phone number and encoded message
    const whatsappNumber = "+93729599599"; // Updated to valid international format
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp in a new tab
    window.open(whatsappUrl, "_blank");

    // Close the dialog
    closeDialog();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col">
        <div className="relative h-48">
          <img
            src={stock.image || "/placeholder.svg?height=200&width=400&text=Panjshir+Emeralds"}
            alt={stock.name}
            className="w-full h-full object-cover absolute top-0 left-0"
            crossOrigin="anonymous"
          />
          <div className="absolute top-2 right-2 bg-white/90 text-emerald-600 px-2 py-1 rounded-md text-sm font-medium flex items-center">
            <TrendingUp className="h-4 w-4 mr-1" />
            {stock.return_rate}% Return
          </div>
        </div>
        <div className="p-5 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold">{stock.name}</h3>
              <p className="text-sm text-gray-500">{stock.location}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-emerald-600">${formatPrice(stock.price)}</div>
              <p className="text-sm text-gray-500">minimum investment</p>
            </div>
          </div>
        </div>
        <div className="p-5 flex-grow">
          <p className="text-gray-600 mb-4">{stock.description}</p>
          <div className="space-y-3">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-500">Investment Duration</p>
              <p className="font-medium">{stock.duration}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-500">Investment Focus</p>
              <p className="font-medium">{stock.focus}</p>
            </div>
          </div>
        </div>
        <div className="p-5 border-t">
          <div className="grid grid-cols-2 gap-3">
            <button
              disabled
              className="bg-gray-300 text-gray-500 px-4 py-3 rounded-md font-medium transition-colors flex items-center justify-center cursor-not-allowed"
            >
              Credit Card
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </button>
            <button
              onClick={openDialog}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-md font-medium transition-colors flex items-center justify-center"
            >
              WhatsApp
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Custom Dialog */}
        {isDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold">Invest in {stock.name} via WhatsApp</h3>
                    <p className="text-sm text-gray-500">
                      Complete the form below to initiate your investment via WhatsApp.
                    </p>
                  </div>
                  <button onClick={closeDialog} className="text-gray-400 hover:text-gray-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <label htmlFor="investment" className="block text-sm font-medium text-gray-700">
                      Investment Amount
                    </label>
                    <div className="bg-gray-50 p-3 rounded-md font-medium">${formatPrice(stock.price)}</div>
                    <p className="text-xs text-gray-500">
                      This is the minimum investment amount for this tier. You can discuss larger investments via
                      WhatsApp.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <label htmlFor="payment" className="block text-sm font-medium text-gray-700">
                      Payment Method
                    </label>
                    <select
                      id="payment"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cryptocurrency">Cryptocurrency</option>
                      <option value="Wire Transfer">Wire Transfer</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={closeDialog}
                    className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWhatsAppSubmit}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                  >
                    Contact via WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}